import type {
    ProficiencyLevelResponse,
    VesselDepartmentResponse,
} from '@routes/sidebar/admin-center/___shared___/api/rankReference.api.ts';
import type { VesselGroup } from '@routes/sidebar/admin-center/___shared___/api/vesselGroup.api.ts';
import type { VesselTypes } from '@routes/sidebar/admin-center/___shared___/api/vesselTypes.api.ts';
import type {
    MatrixRankScope,
    MatrixVesselScope,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/api/matrices.api.ts';
import type {
    Category,
    TransformedSubItemForMatrices,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/types.ts';
import { isNil, uniq } from 'lodash';

export const mapArrToStrings = (value: unknown): string[] => {
    return Array.isArray(value) ? value.map(String) : [];
};

export type MatricesCustomSelectData = {
    levelOneItems: Category[];
    levelTwoItems: Category[];
    subItems: TransformedSubItemForMatrices[];
};

// ranks for matrices
export const buildMatricesRanksSelectData = (
    ranks: TransformedSubItemForMatrices[],
    vesselDepartments: VesselDepartmentResponse[],
    proficiencyLevels: ProficiencyLevelResponse[],
): MatricesCustomSelectData => {
    // only show groups that exist in ranks
    const deptIdsInRanks = new Set(ranks.map((r) => r.type).filter(Boolean));
    const levelIdsInRanks = new Set(ranks.map((r) => r.category).filter(Boolean));

    const levelOneItems: Category[] = (vesselDepartments ?? [])
        .filter((d) => deptIdsInRanks.has(String(d.id)))
        .map((d) => ({ value: String(d.id), label: d.name }));

    const levelTwoItems: Category[] = (proficiencyLevels ?? [])
        .filter((p) => levelIdsInRanks.has(String(p.id)))
        .map((p) => ({ value: String(p.id), label: p.proficiencyLevel }));

    return { levelOneItems, levelTwoItems, subItems: ranks };
};

export const buildMatrixRankScope = (
    selectedRankIds: string[],
    ranks: TransformedSubItemForMatrices[],
): MatrixRankScope => {
    const selectedSet = new Set(selectedRankIds);

    const rankIdsByDepartmentId = new Map<string, string[]>();
    const rankIdsByProficiencyLevelId = new Map<string, string[]>();

    ranks.forEach((rank) => {
        const departmentId = rank.type;
        const proficiencyLevelId = rank.category;

        if (departmentId) {
            const current = rankIdsByDepartmentId.get(departmentId) ?? [];
            current.push(rank.value);
            rankIdsByDepartmentId.set(departmentId, current);
        }

        if (proficiencyLevelId) {
            const current = rankIdsByProficiencyLevelId.get(proficiencyLevelId) ?? [];
            current.push(rank.value);
            rankIdsByProficiencyLevelId.set(proficiencyLevelId, current);
        }
    });

    const departmentIds = uniq(
        [...rankIdsByDepartmentId.entries()]
            .filter(([, rankIdsInDepartment]) => rankIdsInDepartment.every((id) => selectedSet.has(id)))
            .map(([departmentId]) => Number(departmentId))
            .filter(Number.isFinite),
    );
    const proficiencyLevelIds = uniq(
        [...rankIdsByProficiencyLevelId.entries()]
            .filter(([, rankIdsInLevel]) => rankIdsInLevel.every((id) => selectedSet.has(id)))
            .map(([levelId]) => Number(levelId))
            .filter(Number.isFinite),
    );
    const rankReferenceIds = selectedRankIds.map(Number);

    return {
        departmentIds,
        proficiencyLevelIds,
        rankReferenceIds,
    };
};

// vessels for matrices
export const buildMatricesVesselsSelectData = (
    vessels: TransformedSubItemForMatrices[],
    vesselGroups: VesselGroup[],
    vesselTypes: VesselTypes[],
): MatricesCustomSelectData => {
    const groupIdsInUse = new Set(vessels.map((v) => String(v.category)).filter(Boolean));
    const subTypeIdsInUse = new Set(vessels.map((v) => String(v.type)).filter(Boolean));

    const subTypeIdToGroupId = new Map<string, string>();

    vessels.forEach((v) => {
        const subTypeId = String(v.type);
        const groupId = String(v.category);
        if (subTypeId && groupId) subTypeIdToGroupId.set(subTypeId, groupId);
    });

    const levelOneItems: Category[] = vesselGroups
        .filter((g) => groupIdsInUse.has(String(g.id)))
        .filter((g) => !isNil(g.shortCode))
        .map((g) => ({ value: String(g.id), label: g.shortCode ?? '' }));

    const typeIdsInUse = new Set(
        vesselTypes.filter((st) => subTypeIdsInUse.has(String(st.id))).map((st) => String(st.vesselType.id)),
    );

    const levelTwoItems: Category[] = Array.from(
        new Map(
            vesselTypes
                .filter((st) => subTypeIdsInUse.has(String(st.id)))
                .map((st) => [
                    String(st.vesselType.id),
                    { value: String(st.vesselType.id), label: st.vesselType.shortCode ?? '' },
                ]),
        ).values(),
    ).filter((t) => typeIdsInUse.has(String(t.value)));

    const subItems: TransformedSubItemForMatrices[] = vesselTypes
        .filter((st) => subTypeIdsInUse.has(String(st.id)))
        .map((st) => ({
            value: String(st.id),
            label: st.shortCode ?? '',
            type: String(st.vesselType.id),
            category: subTypeIdToGroupId.get(String(st.id)) ?? '',
        }))
        .filter((x) => x.category);

    return { levelOneItems, levelTwoItems, subItems };
};

export const buildMatrixVesselScope = (
    selectedVesselSubTypeIds: string[],
    vessels: TransformedSubItemForMatrices[],
    vesselTypes: VesselTypes[],
): MatrixVesselScope => {
    const selectedSubTypeIdSet = new Set(selectedVesselSubTypeIds);
    const allowedSubTypeIdSet = new Set(vessels.map((v) => v.type).filter(Boolean));
    const totalSubTypesByGroupId: Record<string, number> = {};
    const selectedSubTypesByGroupId: Record<string, number> = {};

    vessels.forEach((v) => {
        const groupId = v.category;
        const subTypeId = v.type;

        if (!groupId || !subTypeId) return;

        totalSubTypesByGroupId[groupId] = (totalSubTypesByGroupId[groupId] ?? 0) + 1;

        if (selectedSubTypeIdSet.has(subTypeId)) {
            selectedSubTypesByGroupId[groupId] = (selectedSubTypesByGroupId[groupId] ?? 0) + 1;
        }
    });

    const vesselGroupIds = Object.keys(totalSubTypesByGroupId)
        .filter((groupId) => (selectedSubTypesByGroupId[groupId] ?? 0) === totalSubTypesByGroupId[groupId])
        .map(Number)
        .filter(Number.isFinite);

    const totalSubTypesByTypeId: Record<string, number> = {};
    const selectedSubTypesByTypeId: Record<string, number> = {};

    vesselTypes.forEach((subType) => {
        const subTypeId = String(subType.id);
        const typeId = String(subType.vesselType?.id ?? '');

        if (!allowedSubTypeIdSet.has(subTypeId) || !typeId) return;

        totalSubTypesByTypeId[typeId] = (totalSubTypesByTypeId[typeId] ?? 0) + 1;

        if (selectedSubTypeIdSet.has(subTypeId)) {
            selectedSubTypesByTypeId[typeId] = (selectedSubTypesByTypeId[typeId] ?? 0) + 1;
        }
    });

    const vesselTypeIds = Object.keys(totalSubTypesByTypeId)
        .filter((typeId) => (selectedSubTypesByTypeId[typeId] ?? 0) === totalSubTypesByTypeId[typeId])
        .map(Number)
        .filter(Number.isFinite);

    const vesselSubTypeIds = selectedVesselSubTypeIds.map(Number).filter(Number.isFinite);

    return {
        vesselGroupIds,
        vesselTypeIds,
        vesselSubTypeIds,
    };
};
